/**
 * Passport role-tag constants and tag-set predicates.
 *
 * Mirrors the Passport `passport-service` role.entity.ts tag scheme — roles
 * carry a comma-separated `tags` string like
 * `"Studio,AIAgent,Default,Observer"`.
 */

export const ROLE_TAG = {
  SuperAdmin: 'SuperAdmin',
  OrgAdmin: 'OrgAdmin',
  AIAgentAdmin: 'AIAgentAdmin',
  Analyst: 'Analyst',
  Observer: 'Observer',
} as const;

export type RoleTag = (typeof ROLE_TAG)[keyof typeof ROLE_TAG];

/** Tags that grant some write access by default. */
export const DEFAULT_WRITE_TAGS: readonly RoleTag[] = [
  ROLE_TAG.SuperAdmin,
  ROLE_TAG.OrgAdmin,
  ROLE_TAG.AIAgentAdmin,
  ROLE_TAG.Analyst,
];

/**
 * Tags that grant admin-tier access. Analyst keeps every other write surface
 * but loses admin-tier actions (e.g. Clone*, OverrideTestRunResult).
 */
export const DEFAULT_ADMIN_TAGS: readonly RoleTag[] = [
  ROLE_TAG.SuperAdmin,
  ROLE_TAG.OrgAdmin,
  ROLE_TAG.AIAgentAdmin,
];

export function hasAny(tags: ReadonlySet<string>, candidates: readonly string[]): boolean {
  for (const candidate of candidates) {
    if (tags.has(candidate)) return true;
  }
  return false;
}

export function hasWriteTag(
  tags: ReadonlySet<string>,
  writeTags: readonly string[] = DEFAULT_WRITE_TAGS
): boolean {
  return hasAny(tags, writeTags);
}

export function hasAdminTag(
  tags: ReadonlySet<string>,
  adminTags: readonly string[] = DEFAULT_ADMIN_TAGS
): boolean {
  return hasAny(tags, adminTags);
}

export function isObserverFromTags(
  tags: ReadonlySet<string>,
  writeTags: readonly string[] = DEFAULT_WRITE_TAGS
): boolean {
  return tags.has(ROLE_TAG.Observer) && !hasWriteTag(tags, writeTags);
}
